import {ICommodity} from 'app/shared/model/commodity.model';
import {IQuotation} from 'app/shared/model/quotation.model';
import {IBank} from 'app/shared/model/bank.model';
import {IBankAgreementDateLog} from 'app/shared/model/bank-agreement-date-log.model';
import {IComexBankFee} from 'app/shared/model/comex-bank-fee.model';

/**
 * Contains various utility methods that contain reusable biz logic for Quotations
 */
export const isLocalCommodity = (commodity: ICommodity | undefined) => {
  return commodity?.isActive === true && commodity?.ownership === 'Local';
};

/**
 * Mirrors FeeCalculationEngine.filterFeesForLumpSumAndPercentage + calculateFeeFromBracket
 * (backend, not touched by this util) purely for display purposes: backend rounds Lump Sum
 * fees to a whole number before they're ever returned/persisted, so this re-derives the real
 * configured decimal amount from the bank's current fee brackets for on-screen display only.
 * Returns null when no bracket matches, or the matching bracket isn't a Lump Sum fee.
 */
export const resolveLumpSumFeeAmount = (
  bankFeeList: IComexBankFee[] | undefined,
  amount: number | undefined | null,
  currencyIdForFiltering: number | undefined | null
): number | null => {
  if (!bankFeeList?.length || amount === undefined || amount === null) return null;

  const relevant = bankFeeList
    .filter(fee => {
      const feeType = fee?.feeType?.toLowerCase();
      if (feeType === 'percentage') return true;
      if (feeType === 'lump sum') return fee.currencyId != null && fee.currencyId === currencyIdForFiltering;
      return false;
    })
    .sort((a, b) => (a.fromTo - b.fromTo) || a.feeType.localeCompare(b.feeType));

  if (!relevant.length) return null;

  const lastIndex = relevant.length - 1;
  for (let i = lastIndex; i >= 0; i--) {
    const current = relevant[i];
    const matches =
      i === lastIndex && amount > relevant[lastIndex].fromTo ? true :
        i === 0 ? amount <= current.fromTo :
          amount <= current.fromTo && amount > relevant[i - 1].fromTo;

    if (matches) {
      return current.feeType.toLowerCase() === 'lump sum' ? current.amount : null;
    }
  }
  return null;
};

// Whether the bank's commission-based fee mode is active for the given local/international flag.
export const isCommissionModeForBank = (isLocal: boolean, bank: IBank | undefined) => {
  return isLocal ? bank?.commissionLocal : bank?.commission;
};

// The CurrencyIndex id to filter Lump Sum fee brackets by: the bank's default currency when
// displayTransactionFee fee currency is "bank default currency", else the selected/transaction currency.
export const resolveCurrencyIdForFiltering = (
  isDefaultCurrency: boolean,
  bankDefaultCurrencyId: number | undefined | null,
  selectedCurrencyIndexId: number | undefined | null
) => {
  return isDefaultCurrency ? bankDefaultCurrencyId : selectedCurrencyIndexId;
};

// A bank with no partner uses the default Alshmokh forms; Boubyan forms only apply when the
// bank's selected trading partner is specifically Boubyan Capital. The partner is set per
// commodity type: local commodity uses bank.localPartner, international uses bank.partner.
export const isBoubyanBank = (bank: IBank | undefined, isLocal: boolean) => {
  const partner = isLocal ? bank?.localPartner : bank?.partner;
  return partner?.name?.toLowerCase().includes('boubyan capital') ?? false;
};

export const isBoubyanQuotation = (quotation: IQuotation | undefined) => {
  return isBoubyanBank(quotation?.bank, quotation?.commodity?.ownership?.toLowerCase() === 'local');
};

export const getStaffFullName = createdUser => {
  if (!createdUser || typeof createdUser !== 'object') {
    return '';
  }

  const { firstName, lastName } = createdUser;
  if (!firstName || !lastName) {
    return '';
  }

  const fullName = `${firstName} ${lastName}`;
  return fullName;
};

export const generateInvoiceNumber = (quotationEntity: IQuotation) => {
  const currentYear = new Date().getFullYear();
  const purchaseOrderNumber = quotationEntity?.purchaseOrder?.poNumber || '00000';
  return `INV/${currentYear}/${purchaseOrderNumber}`;
};

export const numberToWords = num => {
  if (typeof num !== 'number') return '';

  const a = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = n => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' And ' + convert(n % 100) : '');
    if (n < 1000000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' And ' + convert(n % 1000) : '');
    return 'Number too large';
  };

  return convert(num);
};

export const isEditable = (createdDate: Date | null, maxEditableDays: number | null | undefined, currentDate: Date): boolean => {
  if (!createdDate || maxEditableDays === null || maxEditableDays === undefined) {
    return false;
  }

  const editableUntil = new Date(createdDate);
  editableUntil.setDate(createdDate.getDate() + maxEditableDays);

  return currentDate <= editableUntil;
};

// Transaction certificates must show the agreement date that was actually in force when the
// quotation was created, not the bank's current value - the bank's agreement date can be changed
// later, and old certificates must keep reflecting what applied at the time.
export const resolveAgreementDateAtQuotationTime = (
  quotation: IQuotation | undefined,
  agreementDateLogs: IBankAgreementDateLog[] | undefined
): string | null => {
  const isLocal = quotation?.commodity?.ownership?.toLowerCase() === 'local';
  const currentValue = (isLocal ? quotation?.bank?.localAgreementDate : quotation?.bank?.agreementDate) ?? null;

  const quotationCreatedDate = quotation?.createdDate ? new Date(quotation.createdDate) : null;
  if (!quotationCreatedDate || !agreementDateLogs?.length) {
    return currentValue;
  }

  const commodityType = isLocal ? 'local' : 'international';
  const changesAfterCreation = agreementDateLogs
    .filter(l => l.commodity?.toLowerCase() === commodityType && l.createdDate && new Date(l.createdDate) > quotationCreatedDate)
    .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());

  // The first change made after the quotation was created tells us, via its old value,
  // what the agreement date was at the moment the quotation was created.
  if (changesAfterCreation.length > 0) {
    return changesAfterCreation[0].oldValue ?? currentValue;
  }

  // No change has happened since the quotation was created, so the current value still applies.
  return currentValue;
};

export const forceLogoAlignment = (
  containerId: string
): void => {
  const printContent = document.getElementById(containerId);
  if (!printContent) {
    return;
  }

  const logoHeader = printContent.querySelector('.logo-header');
  if (logoHeader instanceof HTMLElement) {
    logoHeader.style.setProperty('text-align', 'left', 'important');
  }
};
